import {
    Assignment,
    Checklist,
    Comment,
    EditCalendar,
    HealthAndSafety,
    Info,
    NotListedLocation,
    Person,
    PersonPin,
    Settings,
} from '@mui/icons-material';
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    Link,
    Link as MuiLink,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ContentCopy, RadioactiveCircle } from 'mdi-material-ui';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
    getActivityPriorities,
    getActivityTypes,
    getBinaryChoice,
    getCompensatoryMeasureTypes,
    getConstraints,
    getMaterialResources,
    getOnSiteActivityStatuses,
} from '../../../api/activities-nim';
import { getAttachment } from '../../../api/attachments';
import { getDimrAlaraLevels, getDimrAreaClassification, getDimrRequirementPeriods } from '../../../api/documents/dimrs';
import {
    ActivityLocationNimReadDTO,
    ActivityNimReadDTO,
    ActivityParticipantNimReadDTO,
    ActivityStatusCode,
    ActivityTestQuestionReadDTO,
    ActivityTypeCode,
    AdamsAccessDTO,
    AdamsAccessStatusDTO,
    AttachmentDTO,
    DocumentType,
    EgroupWithMembersDTO,
    FeatureFlag,
    LinkedDocumentCode,
    ListOfValuesReadDTO,
    LocationDTO,
    ParticipantDoseExceedReadDTO,
    ParticipantRoleCode,
    PersonSmallDTO,
    RadiographyDeviceCode,
    RadiographySourceReadDTO,
} from '../../../api/dto';
import {
    getActivityHazardTypes,
    getDurationTypes,
    getFacilities,
    getProcedureDocuments,
    getRadiographyDeviceTypes,
    getSafetyDocuments,
    getTriggeredAlarmTypes,
    getWorkingTimes,
} from '../../../api/values';
import { ActivityAddSafetyDocumentButton } from '../../../components/documents/ActivityAddSafetyDocumentButton';
import { DocumentButtonsStack } from '../../../components/documents/DocumentButtonsStack';
import { ImpactFormattedDate } from '../../../components/ImpactFormattedDate';
import { TooltipIcon } from '../../../components/tables/table/utils';
import { fetchCernEmployeesByName, fetchEgroups } from '../../../lib/api';
import { WorkOrderIcon } from '../../../lib/information/workOrder';
import { getLocationLabel } from '../../../lib/label';
import { translateValue } from '../../../lib/language';
import { AttachmentsAction } from '../../../pages/documents/ndc/AttachmentsAction';
import { getAdamsUrl, getPhonebookUrl } from '../../../routes/externalRoutes';
import { FetchUseCase } from '../../fields/AsyncAutocompleteElement';
import { Input } from '../../fields/Input';
import { DocumentRedirect } from '../../fields/specific/DocumentRedirect';
import { LocationFacilityWarning } from '../../fields/specific/LocationFacilityWarning';
import { FormElementCommon, LayoutGridItem, LayoutResponsiveItem, TFormElement } from '../../types';
import { UiSchemaType } from '../../UiSchemaType';
import { joinFieldPath, splitFieldPath } from '../../utils';
import { locationAccessPointSchema } from '../accessPoints';
import { activityParticipantRoleSchema } from '../activityParticipantRoles';
import { contractsSchema } from '../contracts';
import { dimrSchema } from '../dimr';
import { equipmentSchema } from '../equipment';
import { interventionPeriodSchema } from '../interventionPeriod';
import { locationSchema } from '../location';
import { personSchema, sortPeopleAlphabeticallySmallDto } from '../person';
import { prefilledLovSchema } from '../prefilledLovSchema';
import { radiographySourceSchema } from '../radiographySource';
import { systemSchema } from '../system';
import { numberWithUnitSchemaFor } from '../unit';
import { valueSchema } from '../value';
import { workOrdersSchema } from '../workOrders';

const URL_EDMS = 'https://edms.cern.ch/ui/#!master/navigator/document?D:100296536:100296536:subDocs';
const URL_GSI_SH_1 = 'https://edms.cern.ch/ui/#!master/navigator/document?D:1011224406:1011224406:subDocs';

interface AdamsAccessWidgetProps {
    activityStatus: ActivityStatusCode;
    accessPointsNames: string[];
    adamsData: AdamsAccessDTO[] | undefined;
    doseExceedInfo: ParticipantDoseExceedReadDTO[] | undefined;
    path: string;
}

const AccessStatusColor: Record<AdamsAccessStatusDTO, 'success' | 'warning' | 'error' | 'info'> = {
    [AdamsAccessStatusDTO.EMPTY]: 'info',
    [AdamsAccessStatusDTO.NONE]: 'error',
    [AdamsAccessStatusDTO.PARTIAL]: 'warning',
    [AdamsAccessStatusDTO.ALL]: 'success',
};

const AdamsAccessWidget: React.FC<AdamsAccessWidgetProps> = ({
    activityStatus,
    accessPointsNames,
    adamsData,
    doseExceedInfo,
    path,
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const { watch } = useFormContext();
    const participant: ActivityParticipantNimReadDTO = watch(path);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleOpen = useCallback(
        (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
        [setAnchorEl]
    );
    const handleClose = useCallback(() => setAnchorEl(null), [setAnchorEl]);

    const ACCESS_SITUATION_DATA = {
        match: {
            inProgress: {
                label: 'activity.adamsAccess.status.granted',
                background: theme.palette.success.main,
                hover: theme.palette.success.dark,
            },
            fulfilled: {
                label: 'activity.adamsAccess.status.fulfilled',
                background: alpha(theme.palette.success.main, 0.6),
                hover: theme.palette.success.main,
            },
        },
        noMatch: {
            inProgress: {
                label: 'activity.adamsAccess.status.denied',
                background: theme.palette.error.main,
                hover: theme.palette.error.dark,
            },
            fulfilled: {
                label: 'activity.adamsAccess.status.not.fulfilled',
                background: alpha(theme.palette.error.main, 0.6),
                hover: theme.palette.error.main,
            },
        },
    } as const;

    const accessSituationData = (match: boolean) =>
        match
            ? activityStatus === ActivityStatusCode.IN_PROGRESS
                ? ACCESS_SITUATION_DATA.match.inProgress
                : ACCESS_SITUATION_DATA.match.fulfilled
            : activityStatus === ActivityStatusCode.IN_PROGRESS
            ? ACCESS_SITUATION_DATA.noMatch.inProgress
            : ACCESS_SITUATION_DATA.noMatch.fulfilled;

    const getPersonTooltip = (person: PersonSmallDTO) => {
        const records = adamsData?.filter((r) => r.personId === person.id).map((r) => r.accessPointName) || [];

        return (
            <>
                <Typography fontWeight="bold" variant="body1">
                    <MuiLink
                        href={getPhonebookUrl(
                            person.searchLabel.includes('(') ? person.id.toString() : person.searchLabel
                        )}
                        target="_blank"
                    >
                        {person.searchLabel} ({person.id} / {person.orgUnit?.code})
                    </MuiLink>
                </Typography>
                {accessPointsNames.map((ap) => {
                    const match = records.includes(ap);
                    return (
                        <Typography key={ap} variant="body2" padding="1px">
                            {ap}:{' '}
                            <Chip
                                sx={{
                                    backgroundColor: accessSituationData(match).background,
                                    color: 'white',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: accessSituationData(match).hover,
                                    },
                                    height: '25px',
                                }}
                                label={intl.formatMessage({
                                    id: accessSituationData(match).label,
                                })}
                                component={'a'}
                                href={getAdamsUrl(person.id, ap)}
                                target="_blank"
                                clickable
                            />
                        </Typography>
                    );
                })}
                {doseExceedInfo?.find((data) => data.personId === person.id) && (
                    <Alert sx={{ marginTop: '1rem' }} icon={false} severity="error">
                        <FormattedMessage id="activity.field.participant.dose.exceeded" />
                    </Alert>
                )}
            </>
        );
    };

    const getEgroupTooltip = (egroup: EgroupWithMembersDTO) => {
        const memberIds = egroup.members.map((m) => m.id);
        const groupRecords = adamsData?.filter((r) => memberIds.includes(r.personId)) || [];
        const personWithDoseExceed = doseExceedInfo?.filter((r) => memberIds.includes(r.personId)) || [];

        return (
            <>
                <Typography fontWeight="bold" variant="body1">
                    {egroup.displayName}
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            {accessPointsNames.map((ap) => (
                                <TableCell key={ap}>{ap}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortPeopleAlphabeticallySmallDto(egroup.members).map((person) => (
                            <TableRow key={person.id}>
                                <TableCell>
                                    <MuiLink
                                        href={getPhonebookUrl(
                                            person.searchLabel.includes('(') ? person.id.toString() : person.searchLabel
                                        )}
                                        target="_blank"
                                    >
                                        {person.searchLabel}
                                    </MuiLink>
                                </TableCell>
                                {accessPointsNames.map((ap) => {
                                    const match = groupRecords.some(
                                        (r) => r.personId === person.id && r.accessPointName === ap
                                    );
                                    return (
                                        <TableCell key={ap} sx={{ paddingTop: '1px', paddingBottom: '1px' }}>
                                            <Chip
                                                sx={{
                                                    width: '100%',
                                                    backgroundColor: accessSituationData(match).background,
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    '&:hover': {
                                                        backgroundColor: accessSituationData(match).hover,
                                                    },
                                                    height: '25px',
                                                }}
                                                label={intl.formatMessage({
                                                    id: accessSituationData(match).label,
                                                })}
                                                component={'a'}
                                                href={getAdamsUrl(person.id, ap)}
                                                target="_blank"
                                                clickable
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {personWithDoseExceed.length > 0 && (
                    <Alert sx={{ marginTop: '1rem' }} icon={false} severity="error">
                        <FormattedMessage id="activity.field.participant.dose.exceeded" />
                    </Alert>
                )}
            </>
        );
    };

    const tooltipContent = participant.person ? (
        getPersonTooltip(participant.person)
    ) : participant.egroup ? (
        getEgroupTooltip(participant.egroup)
    ) : (
        <></>
    );

    const getGrantedAccessPoints = (personId: number): string[] => {
        return adamsData?.filter((r) => r.personId === personId).map((r) => r.accessPointName) ?? [];
    };

    const getPersonAccessStatus = (personId: number, requiredPoints: string[]): AdamsAccessStatusDTO => {
        const granted = getGrantedAccessPoints(personId);
        const matched = requiredPoints.filter((ap) => granted.includes(ap)).length;

        if (matched === 0) return AdamsAccessStatusDTO.NONE;
        if (matched === requiredPoints.length) return AdamsAccessStatusDTO.ALL;
        return AdamsAccessStatusDTO.PARTIAL;
    };

    const getEgroupAccessStatus = (memberIds: number[], requiredPoints: string[]): AdamsAccessStatusDTO => {
        const { total, granted } = memberIds.reduce(
            (acc, id) => {
                const personGranted = getGrantedAccessPoints(id);
                requiredPoints.forEach((ap) => {
                    acc.total += 1;
                    if (personGranted.includes(ap)) acc.granted += 1;
                });
                return acc;
            },
            { total: 0, granted: 0 }
        );

        if (granted === 0) return AdamsAccessStatusDTO.NONE;
        if (granted === total) return AdamsAccessStatusDTO.ALL;
        return AdamsAccessStatusDTO.PARTIAL;
    };

    const getIconColor = (): 'success' | 'warning' | 'error' | 'info' => {
        if (participant.person) {
            return AccessStatusColor[getPersonAccessStatus(participant.person.id, accessPointsNames)];
        }

        if (participant.egroup) {
            const memberIds = participant.egroup.members.map((m) => m.id);
            return AccessStatusColor[getEgroupAccessStatus(memberIds, accessPointsNames)];
        }

        return AccessStatusColor[AdamsAccessStatusDTO.EMPTY];
    };

    const iconColor = getIconColor();
    const buttonsStyle = { minWidth: { xs: '100%', md: 0 }, height: '100%' };

    const accessIndicator = (
        <Box display="flex" alignItems="center">
            <Button disabled={iconColor === 'info'} onClick={handleOpen} color={iconColor} sx={buttonsStyle}>
                <Person />
            </Button>
        </Box>
    );

    if (participant.role?.code === ParticipantRoleCode.COMPANY) return null;
    return (
        <Box alignContent="center" height="100%">
            <>
                {iconColor !== 'info' ? (
                    <Tooltip title={intl.formatMessage({ id: 'activity.adamsAccess.icon.tooltip' })} placement="top">
                        {accessIndicator}
                    </Tooltip>
                ) : (
                    <>{accessIndicator}</>
                )}
            </>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2, maxWidth: '90%', overflow: 'auto' } }}
            >
                {tooltipContent}
            </Popover>
        </Box>
    );
};

interface EgroupInactiveWarningProps {
    path: string;
}

type ExternalLink = {
    visible: boolean;
    href?: string;
    text?: string;
};

const EgroupInactiveWarning: React.FC<EgroupInactiveWarningProps> = ({ path }) => {
    const { watch } = useFormContext();
    const participant: ActivityParticipantNimReadDTO = watch(path);
    const egroup = participant?.egroup;

    if (!egroup || egroup.isActive) return null;
    return (
        <Alert severity="warning">
            <FormattedMessage id="activity.field.participant.egroup.inactive" />
        </Alert>
    );
};

const RadiographyAffectedZonePreview: React.FC = () => {
    const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
    const { watch, getValues } = useFormContext();
    const activityAttachments: AttachmentDTO[] = watch('attachments');
    const radiographyAffectedZoneId: number = watch('radiographyAffectedZoneId');
    const radiographyAffectedZoneAttachment = activityAttachments?.find(
        (attachment) => attachment.id === radiographyAffectedZoneId
    );
    const isImage = IMAGE_MIME_TYPES.includes(radiographyAffectedZoneAttachment?.mimeType ?? '');
    const { data: blob } = useQuery({
        queryKey: ['attachment', radiographyAffectedZoneAttachment?.id],
        queryFn: () => getAttachment({ id: radiographyAffectedZoneAttachment!.id }),
        enabled: !!radiographyAffectedZoneAttachment && isImage,
    });

    const imageUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : undefined), [blob]);

    if (!radiographyAffectedZoneAttachment || !isImage) {
        return null;
    }

    if (!imageUrl) {
        return <CircularProgress />;
    }

    return (
        <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <img
                src={imageUrl}
                alt={radiographyAffectedZoneAttachment.originalFileName}
                style={{
                    maxWidth: '100%',
                    maxHeight: 500,
                    objectFit: 'contain',
                }}
            />
        </Box>
    );
};

const RadiographySourceLink: React.FC = () => {
    const GESO_URL = 'https://cern.ch/geso/SourceForm.aspx?ID=';
    const MTF_URL = 'https://edms.cern.ch/equipment/';
    const { watch } = useFormContext();
    const radiographySourceData = watch('radiographySource');
    const deviceType = watch('radiographyDeviceType');

    const setExternalLinks = (radiographySource: RadiographySourceReadDTO) => {
        if (deviceType?.code != RadiographyDeviceCode.XRAY && radiographySource) {
            const geso: ExternalLink = {
                href: GESO_URL + radiographySource.id,
                text: `GESO: ${radiographySource.sourceLabel}`,
                visible: true,
            };

            const mtf: ExternalLink = {
                visible: !!radiographySource.trecNumber,
                href: radiographySource.trecNumber ? MTF_URL + radiographySource.trecNumber : '',
                text: radiographySource.trecNumber ? `MTF: ${radiographySource.trecNumber}` : '',
            };

            return { geso, mtf };
        }

        return {
            geso: { visible: false },
            mtf: { visible: false },
        };
    };

    if (deviceType?.code === RadiographyDeviceCode.XRAY || !radiographySourceData) return null;
    const { geso, mtf } = setExternalLinks(radiographySourceData);

    return (
        <>
            {geso.visible && (
                <Link href={geso.href} target="_blank" rel="noopener noreferrer">
                    {geso.text}
                </Link>
            )}
            {mtf.visible && (
                <Link href={mtf.href} target="_blank" rel="noopener noreferrer">
                    {mtf.text}
                </Link>
            )}
        </>
    );
};

const uiSchemaParticipant = (
    intl: IntlShape,
    activityStatus: ActivityStatusCode,
    adamsData: AdamsAccessDTO[] | undefined,
    accessPointsNames: string[],
    doseExceedInfo: ParticipantDoseExceedReadDTO[] | undefined
): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 12,
            sm: accessPointsNames.length > 0 ? 11.5 : 12,
            element: {
                type: UiSchemaType.LAYOUT_RESPONSIVE,
                typeProperties: {
                    direction: {
                        xs: 'column',
                    },
                },
                elements: [
                    {
                        element: {
                            type: UiSchemaType.CUSTOM,
                            Component: (props) => <EgroupInactiveWarning path={(props as any).path} {...props} />,
                        },
                    },
                    {
                        element: {
                            type: UiSchemaType.LAYOUT_RESPONSIVE,
                            typeProperties: {
                                direction: {
                                    xs: 'column',
                                    sm: 'row',
                                },
                            },
                            elements: [
                                {
                                    element: activityParticipantRoleSchema(
                                        {
                                            label: intl.formatMessage({ id: 'activity.field.role' }),
                                            path: 'role',
                                            endAdornment: () => (
                                                <TooltipIcon
                                                    tooltip={intl.formatMessage({
                                                        id: 'activity.field.role.info',
                                                    })}
                                                />
                                            ),
                                        },
                                        intl.locale
                                    ),
                                },
                                {
                                    element: {
                                        type: UiSchemaType.LAYOUT_RESPONSIVE,
                                        typeProperties: {
                                            direction: {
                                                xs: 'column',
                                                sm: 'row',
                                            },
                                        },
                                        elements: [
                                            {
                                                element: personSchema({
                                                    label: intl.formatMessage({ id: 'activity.field.participant' }),
                                                    path: 'person',
                                                }),
                                            },
                                            {
                                                element: contractsSchema({
                                                    path: 'companyContract',
                                                    label: 'Contract',
                                                }),
                                            },
                                        ],
                                    },
                                },
                            ],
                        },
                    },
                    {
                        element: {
                            type: UiSchemaType.LAYOUT_RESPONSIVE,
                            typeProperties: {
                                direction: {
                                    xs: 'column',
                                    sm: 'row',
                                },
                            },
                            elements: [
                                {
                                    element: {
                                        type: UiSchemaType.AUTOCOMPLETE_ASYNC,
                                        label: 'E-group',
                                        path: 'egroup',
                                        typeProperties: {
                                            fetchOptions: (searchTerm: string) => fetchEgroups(searchTerm),
                                            fetchUseCases: [FetchUseCase.ON_KEYSTROKE],
                                            getOptionLabel: (value: { name: string }) => value.name,
                                            multiple: false,
                                            autocompleteProps: {
                                                isOptionEqualToValue: (
                                                    option: { name: string },
                                                    value: {
                                                        name: string;
                                                    }
                                                ) => {
                                                    return option.name === value.name;
                                                },
                                            },
                                        },
                                    },
                                },
                                {
                                    element: {
                                        type: UiSchemaType.INPUT,
                                        label: 'Unknown',
                                        path: 'unknown',
                                        typeProperties: {
                                            multiline: true,
                                        },
                                    },
                                },
                            ],
                        },
                    },
                ],
            },
        },
        ...(accessPointsNames.length > 0
            ? [
                  {
                      xs: 12,
                      sm: 0.5,
                      element: {
                          type: UiSchemaType.CUSTOM,
                          Component: (props) =>
                              AdamsAccessWidget({
                                  activityStatus,
                                  path: '',
                                  ...props,
                                  adamsData,
                                  accessPointsNames,
                                  doseExceedInfo,
                              }) as React.ReactNode,
                      },
                  } as LayoutGridItem,
              ]
            : []),
    ],
});

const uiSchemaWhereLocation = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 12,
            element: {
                type: UiSchemaType.CUSTOM,
                Component: (props) => {
                    const path: string | undefined = (props as any).path;
                    const { watch } = useFormContext();
                    const activityLocation: ActivityLocationNimReadDTO | undefined = watch(path ?? '_');
                    if (!activityLocation) return null;
                    const shouldShowWarning =
                        activityLocation.location?.accessPoints?.length > 0 &&
                        activityLocation.accessPoints?.length === 0;

                    return shouldShowWarning ? (
                        <Alert severity="warning">
                            <FormattedMessage id={'activity.location.accessPointsEmpty'} />
                        </Alert>
                    ) : null;
                },
            },
        },
        {
            xs: 12,
            sm: 7,
            element: locationSchema({
                label: intl.formatMessage({ id: 'document.field.location' }),
                path: 'location',
                watchPaths: ['facility'],
                allowAnyLocation: false,
                restrictToFacility: true,
                tooltip: intl.formatMessage({
                    id: 'document.field.location.info',
                }),
            }),
        },
        {
            xs: 12,
            sm: 5,
            element: locationAccessPointSchema({
                label: intl.formatMessage({ id: 'document.location.info.accessPoints' }),
                path: 'accessPoints',
            }),
        },
        {
            xs: 12,
            sm: 7,
            element: {
                type: UiSchemaType.LAYOUT_RESPONSIVE,
                typeProperties: {
                    direction: {
                        xs: 'row',
                    },
                },
                elements: [
                    {
                        element: numberWithUnitSchemaFor(
                            {
                                label: 'Coordinates (Swisstopo - MN95)',
                                path: 'coordinateMn95east',
                                unit: 'East',
                                integer: false,
                                signed: false,
                            },
                            intl
                        ),
                    },
                    {
                        element: numberWithUnitSchemaFor(
                            {
                                label: 'Coordinates (Swisstopo - MN95)',
                                path: 'coordinateMn95north',
                                unit: 'North',
                                integer: false,
                                signed: false,
                            },
                            intl
                        ),
                    },
                ],
            },
        },
    ],
});

const uiSchemaActivityHazard = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 12,
            element: valueSchema(
                {
                    label: intl.formatMessage({ id: 'document.field.type' }),
                    path: 'type',
                    fetchOptions: getActivityHazardTypes,
                    multiple: false,
                },
                intl.locale
            ),
        },
        {
            xs: 12,
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'document.panel.comments' }),
                path: 'commentHazardType',
                typeProperties: {
                    multiline: true,
                },
            },
        },
        {
            xs: 12,
            element: {
                type: UiSchemaType.FIELD_ARRAY,
                path: 'compensatoryMeasures',
                element: {
                    type: UiSchemaType.LAYOUT_GRID,
                    elements: [
                        {
                            xs: 12,
                            element: valueSchema(
                                {
                                    label: intl.formatMessage({ id: 'document.field.type' }),
                                    path: 'type',
                                    fetchOptions: getCompensatoryMeasureTypes,
                                },
                                intl.locale
                            ),
                        },
                        {
                            xs: 12,
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'document.panel.comments' }),
                                path: 'comments',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                    ],
                },
                appendLabel: intl.formatMessage({
                    id: 'activity.field.activityHazards.addNewCompensatoryMeasure',
                }),
                removeLabel: intl.formatMessage({
                    id: 'activity.field.activityHazards.removeCompensatoryMeasure',
                }),
            },
        },
    ],
});

const uiSchemaLocationHazards = (intl: IntlShape, activityLocations: LocationDTO[]): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 6,
            element: {
                type: UiSchemaType.AUTOCOMPLETE,
                label: intl.formatMessage({ id: 'document.field.location' }),
                path: 'location',
                typeProperties: {
                    options: activityLocations,
                    getOptionLabel: getLocationLabel,
                    strongValidateOptions: true,
                },
            },
        },
        {
            xs: 6,
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'document.field.type' }),
                path: 'hazard',
            },
        },
        {
            xs: 12,
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'activity.field.compensatoryMeasure' }),
                path: 'compensatoryMeasure',
                typeProperties: {
                    multiline: true,
                },
            },
        },
    ],
});

const uiSchemaCoactivityHazard = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 12,
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'document.field.type' }),
                path: 'type',
            },
        },
        {
            xs: 12,
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'activity.field.compensatoryMeasure' }),
                path: 'compensatoryMeasure',
                typeProperties: {
                    multiline: true,
                },
            },
        },
    ],
});

const uiSchemaProcedureDocuments = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 6,
            element: valueSchema(
                {
                    label: intl.formatMessage({ id: 'document.field.type' }),
                    path: 'documentType',
                    fetchOptions: getProcedureDocuments,
                    multiple: false,
                },
                intl.locale
            ),
        },
        {
            xs: 6,
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'activity.field.documentNumber' }),
                path: 'documentNumber',
                typeProperties: {
                    endAdornment: ({ field }) => {
                        const { value: edmsNumber } = field as {
                            value: string | undefined;
                        };
                        const { watch } = useFormContext();
                        const path = joinFieldPath([...splitFieldPath(field.name).slice(0, 2), 'documentType']);
                        const documentType: ListOfValuesReadDTO<LinkedDocumentCode> | undefined = watch(path);
                        return <DocumentRedirect value={edmsNumber} documentType={documentType?.code} />;
                    },
                },
            },
        },
    ],
});
const uiRpAssessment = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    disabled: true,
    elements: [
        {
            xs: 6,
            element: dimrSchema(
                {
                    path: 'rpAssessment.dimrVersion',
                    label: intl.formatMessage({ id: 'dimr.name' }),
                },
                intl
            ),
        },
        {
            xs: 6,
            element: prefilledLovSchema({
                label: intl.formatMessage({ id: 'document.field.alaraLevel' }),
                path: 'rpAssessment.dimrVersion.alaraLevel',
                fetchOptions: getDimrAlaraLevels,
            }),
        },
        {
            xs: 6,
            element: numberWithUnitSchemaFor(
                {
                    label: intl.formatMessage({
                        id: 'dimr.field.rpa.totalCollectiveWorkTime',
                    }),
                    path: 'rpAssessment.totalCollectiveWorkTime',
                    unit: intl.formatMessage({ id: 'document.unit.personHour' }),
                    integer: false,
                    signed: false,
                },
                intl
            ),
        },
        {
            xs: 6,
            element: numberWithUnitSchemaFor(
                {
                    label: intl.formatMessage({
                        id: 'dimr.field.rpa.maxIndividualWorkTime',
                    }),
                    path: 'rpAssessment.maxIndividualWorkTime',
                    unit: intl.formatMessage({ id: 'document.unit.hour' }),
                    integer: false,
                    signed: false,
                },
                intl
            ),
        },
        {
            xs: 6,
            element: prefilledLovSchema({
                label: intl.formatMessage({ id: 'dimr.field.rpa.highestAreaClassification' }),
                path: 'rpAssessment.highestAreaClassification',
                fetchOptions: getDimrAreaClassification,
            }),
        },
        {
            xs: 6,
            element: valueSchema(
                {
                    label: intl.formatMessage({ id: 'dimr.field.rpa.rpPresence' }),
                    path: 'rpAssessment.rpPresence',
                    fetchOptions: getDimrRequirementPeriods,
                },
                intl.locale
            ),
        },
        {
            xs: 6,
            element: {
                type: UiSchemaType.CHECKBOX,
                label: intl.formatMessage({ id: 'dimr.field.rpa.contaminatingWorks' }),
                path: 'rpAssessment.contaminatingWorks',
            },
        },
        {
            xs: 6,
            element: {
                type: UiSchemaType.CHECKBOX,
                label: intl.formatMessage({ id: 'dimr.field.rpa.opDosimetryMandatory' }),
                path: 'rpAssessment.operDosimetryMandatory',
            },
        },
    ],
});

const uiSchemaSafetyDocumentsExternal = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 6,
            element: valueSchema(
                {
                    label: intl.formatMessage({ id: 'document.field.type' }),
                    path: 'documentType',
                    fetchOptions: getSafetyDocuments,
                    multiple: false,
                    endAdornment: () => (
                        <TooltipIcon
                            tooltip={intl.formatMessage({
                                id: 'activity.field.activityHazards.safetyDocumentExternal.type.info',
                            })}
                        />
                    ),
                },
                intl.locale
            ),
        },
        {
            xs: 6,
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'activity.field.documentNumber' }),
                path: 'documentNumber',
                typeProperties: {
                    endAdornment: ({ field }) => {
                        const { value: edmsNumber } = field as {
                            value: string | undefined;
                        };
                        return <DocumentRedirect value={edmsNumber} documentType={LinkedDocumentCode.EDMS_PROCEDURE} />;
                    },
                },
            },
        },
    ],
});
const TestQuestionType: React.FC<FormElementCommon> = (props) => {
    const intl = useIntl();
    const { watch } = useFormContext();
    const path: string | undefined = (props as any).path;
    const value: ActivityTestQuestionReadDTO | undefined = watch(path ?? '_'); // Dummy property that never gets resolved to
    // satisfy the hook
    const display = path !== undefined && value !== undefined ? translateValue(value.type, intl.locale) : undefined;
    const { disabled } = props;
    return display !== undefined ? (
        <Box display="flex" alignItems="center" height="100%">
            <Typography sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}>{display.label}</Typography>
        </Box>
    ) : null;
};

const uiSchemaActivityTest = (intl: IntlShape, areTestQuestionsDisabled: boolean): TFormElement => ({
    type: UiSchemaType.LAYOUT_GRID,
    elements: [
        {
            xs: 4,
            lg: 5,
            element: {
                type: UiSchemaType.CUSTOM,
                Component: (props) => (
                    <TestQuestionType
                        {...props}
                        disabled={props.disabled === undefined ? areTestQuestionsDisabled : props.disabled}
                    />
                ),
            },
        },
        {
            xs: 2,
            lg: 1,
            element: {
                type: UiSchemaType.CHECKBOX,
                path: 'answer',
                label: '',
            },
        },
        {
            xs: 6,
            lg: 6,
            element: {
                type: UiSchemaType.INPUT,
                path: 'comments',
                label: intl.formatMessage({ id: 'activity.field.comment' }),
            },
        },
    ],
});

interface LogbookEntryAttachmentsProps {
    path: string;
    disabled: boolean;
}

const LogbookEntryAttachments: React.FC<LogbookEntryAttachmentsProps> = ({ path, disabled }) => {
    const { watch } = useFormContext();
    const attachments: AttachmentDTO[] = watch(`${path}.attachments`) ?? [];
    const unsupportedOperation = (): never => {
        throw new Error();
    };
    return (
        <DocumentButtonsStack disabled={disabled} large={true}>
            <AttachmentsAction
                attachments={attachments}
                linkAttachments={unsupportedOperation}
                unlinkAttachments={unsupportedOperation}
                updateAttachment={unsupportedOperation}
                readOnly
            />
        </DocumentButtonsStack>
    );
};

const uiSchemaLogBookEntry = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_RESPONSIVE,
    typeProperties: {
        direction: {
            xs: 'column',
        },
    },
    elements: [
        {
            element: {
                type: UiSchemaType.LAYOUT_RESPONSIVE,
                typeProperties: {
                    direction: {
                        xs: 'column',
                        md: 'row',
                    },
                },
                elements: [
                    {
                        element: personSchema({
                            path: 'createdBy',
                            label: intl.formatMessage({ id: 'document.field.creator' }),
                            disabled: true,
                        }),
                    },
                    {
                        element: {
                            type: UiSchemaType.DATETIME,
                            label: intl.formatMessage({ id: 'document.field.createdDate' }),
                            path: 'createdDate',
                            disabled: true,
                        },
                    },
                ],
            },
        },
        {
            element: {
                type: UiSchemaType.INPUT,
                label: intl.formatMessage({ id: 'document.field.comment' }),
                path: 'text',
                disabled: true,
                typeProperties: {
                    multiline: true,
                },
            },
        },
        {
            element: {
                type: UiSchemaType.LAYOUT_RESPONSIVE,
                typeProperties: {
                    direction: {
                        xs: 'column',
                        md: 'row',
                    },
                },
                elements: [
                    {
                        element: valueSchema(
                            {
                                label: intl.formatMessage({ id: 'document.field.status' }),
                                path: 'status',
                                fetchOptions: getOnSiteActivityStatuses,
                                disabled: true,
                            },
                            intl.locale
                        ),
                    },
                    {
                        element: locationSchema({
                            label: intl.formatMessage({ id: 'document.field.location' }),
                            path: 'location',
                            disabled: true,
                        }),
                    },
                    {
                        element: {
                            type: UiSchemaType.CHECKBOX,
                            label: intl.formatMessage({ id: 'activity.field.logbook.warning' }),
                            path: 'warning',
                            disabled: true,
                        },
                    },
                    {
                        element: {
                            type: UiSchemaType.CHECKBOX,
                            label: intl.formatMessage({ id: 'activity.field.logbook.notifyActivityResponsible' }),
                            path: 'notifyActivityResponsible',
                            disabled: true,
                        },
                    },
                ],
            },
        },
        {
            element: {
                type: UiSchemaType.CUSTOM,
                Component: (props) => <LogbookEntryAttachments {...(props as any)} />,
            },
        },
    ],
});

export const activityFormSchema = (
    intl: IntlShape,
    activityId: number | null,
    isCreate: boolean,
    copyProposedDate: React.Dispatch<any>,
    copyPropDateButtonShown: boolean,
    proposedEndDate: Date | null,
    activityLocations: LocationDTO[],
    accessPointsNames: string[],
    isUrgent: boolean,
    isCreatingChangeRequest: boolean,
    activityStatus: ActivityStatusCode,
    adamsData: AdamsAccessDTO[] | undefined,
    attachments: AttachmentDTO[],
    doseExceedInfo: ParticipantDoseExceedReadDTO[] | undefined,
    areLocationsPartOfFacility: boolean,
    isSystemPartOfFacility: boolean,
    areTestQuestionsDisabled: boolean,
    proposedDatesNotInIntPeriod: boolean,
    scheduledDatesNotInIntPeriod: boolean,
    isParticipantListEmpty: boolean,
    showProposalPanel: boolean,
    rpAssessmentLinked: boolean,
    isEspFormEnabled: boolean,
    isFromEspTemplate: boolean,
    linkedSafetyDocuments: Pick<
        ActivityNimReadDTO,
        'notesDeCoupure' | 'firePermits' | 'is37s' | 'vics' | 'lockouts' | 'rpAssessment'
    > | null,
    canLinkSafetyDocuments: boolean,
    refreshData: () => void,
    renderSafetyApprovalCheckbox: boolean,
    features: { [P in FeatureFlag]?: boolean }
): TFormElement => {
    return {
        type: UiSchemaType.LAYOUT_VERTICAL,
        elements: [
            {
                type: UiSchemaType.PANEL,
                label: intl.formatMessage({ id: 'document.panel.general' }),
                typeProperties: {
                    icon: <Settings color="action" />,
                },
                element: {
                    type: UiSchemaType.LAYOUT_GRID,
                    elements: [
                        {
                            xs: 12,
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'document.field.title' }),
                                path: 'title',
                                typeProperties: {
                                    endAdornment: () => (
                                        <TooltipIcon
                                            tooltip={intl.formatMessage({
                                                id: 'document.field.title.info',
                                            })}
                                        />
                                    ),
                                },
                            },
                        },
                        {
                            xs: 6,
                            element: personSchema({
                                label: intl.formatMessage({ id: 'document.field.responsible' }),
                                path: 'activityResponsible',
                                fetchEndpoint: fetchCernEmployeesByName,
                                tooltip: intl.formatMessage({
                                    id: 'document.field.responsible.info',
                                }),
                            }),
                        },
                        {
                            xs: 6,
                            element: valueSchema(
                                {
                                    label: intl.formatMessage({ id: 'document.field.facility' }),
                                    path: 'facility',
                                    fetchOptions: getFacilities,
                                    multiple: false,
                                    endAdornment: () => (
                                        <TooltipIcon
                                            tooltip={intl.formatMessage({
                                                id: 'document.field.facility.info',
                                            })}
                                        />
                                    ),
                                },
                                intl.locale
                            ),
                        },
                        {
                            xs: 12,
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                typeProperties: {
                                    direction: {
                                        xs: 'row',
                                    },
                                },
                                elements: [
                                    {
                                        element: valueSchema(
                                            {
                                                label: intl.formatMessage({ id: 'activity.field.activityTypes' }),
                                                path: 'type',
                                                fetchOptions: () => getActivityTypes({ selectable: true }),
                                                multiple: false,
                                                endAdornment: () => {
                                                    const formContext = useFormContext();
                                                    const activityType = formContext.watch('type');
                                                    return activityType?.code ===
                                                        ActivityTypeCode.PROFESSIONAL_VISIT ? (
                                                        <TooltipIcon
                                                            tooltip={intl.formatMessage({
                                                                id: 'activity.field.activityTypes.professionalVisits',
                                                            })}
                                                            documentUrl={URL_GSI_SH_1}
                                                        />
                                                    ) : null;
                                                },
                                            },
                                            intl.locale
                                        ),
                                    },
                                    {
                                        element: {
                                            type: UiSchemaType.LAYOUT_RESPONSIVE,
                                            typeProperties: {
                                                direction: {
                                                    xs: 'column',
                                                },
                                            },
                                            elements: [
                                                {
                                                    element: valueSchema(
                                                        {
                                                            label: intl.formatMessage({
                                                                id: 'activity.field.priority',
                                                            }),
                                                            path: 'priority',
                                                            fetchOptions: getActivityPriorities,
                                                            multiple: false,
                                                        },
                                                        intl.locale
                                                    ),
                                                },
                                                ...(renderSafetyApprovalCheckbox
                                                    ? [
                                                          {
                                                              element: {
                                                                  type: UiSchemaType.CHECKBOX,
                                                                  label: intl.formatMessage({
                                                                      id: 'activity.field.notRequiresSafetyApproval',
                                                                  }),
                                                                  path: 'notRequiresSafetyApproval',
                                                              },
                                                          } satisfies LayoutGridItem,
                                                      ]
                                                    : []),
                                            ],
                                        },
                                    },
                                ],
                            },
                        },
                    ],
                },
            },
            {
                type: UiSchemaType.PANEL,
                label: intl.formatMessage({ id: 'activity.panel.what' }),
                typeProperties: {
                    icon: <Comment color="action" />,
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    typeProperties: {
                        direction: {
                            xs: 'column',
                        },
                    },
                    elements: [
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'document.field.description' }),
                                path: 'description',
                                typeProperties: {
                                    multiline: true,
                                    endAdornment: () => (
                                        <TooltipIcon
                                            tooltip={intl.formatMessage({
                                                id: 'document.field.description.info',
                                            })}
                                        />
                                    ),
                                },
                            },
                        },
                        ...(!isSystemPartOfFacility
                            ? [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: () => (
                                              <Alert severity="warning">
                                                  <FormattedMessage id={'activity.systemFacility'} />
                                              </Alert>
                                          ),
                                      },
                                  } satisfies LayoutGridItem,
                              ]
                            : []),
                        {
                            element: systemSchema({
                                label: intl.formatMessage({ id: 'activity.field.system' }),
                                path: 'system',
                                restrictToFacility: true,
                                watchPaths: ['facility'],
                            }),
                        },
                    ],
                },
            },
            {
                type: UiSchemaType.PANEL,
                label: intl.formatMessage({ id: 'activity.panel.where' }),
                typeProperties: {
                    icon: <NotListedLocation color="action" />,
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    typeProperties: {
                        direction: {
                            xs: 'column',
                        },
                    },
                    elements: [
                        ...(accessPointsNames.length === 0
                            ? [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: () => (
                                              <Alert severity="warning">
                                                  <FormattedMessage id={'activity.location.noAccessPointSelected'} />
                                              </Alert>
                                          ),
                                      },
                                  } satisfies LayoutGridItem,
                              ]
                            : []),
                        ...(!areLocationsPartOfFacility
                            ? [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: (props) => (
                                              <LocationFacilityWarning
                                                  {...(props as any)}
                                                  documentType={DocumentType.ACTIVITY}
                                              />
                                          ),
                                      },
                                  } satisfies LayoutGridItem,
                              ]
                            : []),
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'activityLocations',
                                element: uiSchemaWhereLocation(intl),
                                appendLabel: intl.formatMessage({ id: 'activity.field.location.addNewLocation' }),
                                removeLabel: intl.formatMessage({ id: 'activity.field.location.removeLocation' }),
                                initialValue: {
                                    location: null,
                                    accessPoints: null,
                                },
                                hideButtons: isUrgent && isCreatingChangeRequest,
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'document.panel.comments' }),
                                path: 'whereComments',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                        {
                            element: valueSchema(
                                {
                                    label: intl.formatMessage({
                                        id: 'activity.field.impactedFacilities',
                                    }),
                                    path: 'impactedFacilities',
                                    fetchOptions: getFacilities,
                                    multiple: true,
                                    endAdornment: () => (
                                        <TooltipIcon
                                            tooltip={intl.formatMessage({
                                                id: 'activity.field.impactedFacilities.info',
                                            })}
                                        />
                                    ),
                                },
                                intl.locale
                            ),
                        },
                    ],
                },
            },
            ...(showProposalPanel
                ? [
                      {
                          type: UiSchemaType.PANEL,
                          label: intl.formatMessage({ id: 'activity.panel.when.proposed' }),
                          typeProperties: {
                              icon: <EditCalendar color="action" />,
                              tooltip: intl.formatMessage({
                                  id: 'activity.panel.when.proposed.info',
                              }),
                          },
                          element: {
                              type: UiSchemaType.LAYOUT_RESPONSIVE,
                              elements: [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: () => (
                                              <Alert
                                                  severity="info"
                                                  sx={{ display: proposedEndDate ? 'flex' : 'none' }}
                                              >
                                                  {proposedEndDate ? (
                                                      <FormattedMessage
                                                          id="activity.field.proposedDate.endDateInfo"
                                                          values={{
                                                              proposedEndDate: (
                                                                  <ImpactFormattedDate date={proposedEndDate} />
                                                              ),
                                                          }}
                                                      />
                                                  ) : (
                                                      <></>
                                                  )}
                                              </Alert>
                                          ),
                                      },
                                  } satisfies LayoutResponsiveItem,
                                  ...(proposedDatesNotInIntPeriod
                                      ? [
                                            {
                                                element: {
                                                    type: UiSchemaType.CUSTOM,
                                                    Component: () => (
                                                        <Alert severity="warning">
                                                            <FormattedMessage id="activity.field.proposedInterventionPeriod.warning" />
                                                        </Alert>
                                                    ),
                                                },
                                            } satisfies LayoutResponsiveItem,
                                        ]
                                      : []),
                                  {
                                      element: {
                                          type: UiSchemaType.LAYOUT_RESPONSIVE,
                                          typeProperties: {
                                              direction: {
                                                  xs: 'row',
                                              },
                                          },

                                          elements: [
                                              {
                                                  element: {
                                                      type: UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE,
                                                      label: intl.formatMessage({ id: 'activity.field.proposedDate' }),
                                                      path: 'proposedDate',
                                                  },
                                              },
                                              {
                                                  element: interventionPeriodSchema({
                                                      label: intl.formatMessage({
                                                          id: 'activity.field.proposedInterventionPeriod',
                                                      }),
                                                      path: 'proposedInterventionPeriod',
                                                      multiple: false,
                                                  }),
                                              },
                                          ],
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.LAYOUT_RESPONSIVE,
                                          typeProperties: {
                                              direction: {
                                                  xs: 'row',
                                              },
                                          },
                                          elements: [
                                              {
                                                  element: valueSchema(
                                                      {
                                                          label: intl.formatMessage({
                                                              id: 'activity.field.workingTimeAvail',
                                                          }),
                                                          path: 'workingTimeAvail',
                                                          fetchOptions: getWorkingTimes,
                                                          multiple: false,
                                                      },
                                                      intl.locale
                                                  ),
                                              },
                                              {
                                                  element: {
                                                      type: UiSchemaType.LAYOUT_RESPONSIVE,
                                                      typeProperties: {
                                                          direction: {
                                                              xs: 'row',
                                                          },
                                                      },
                                                      elements: [
                                                          {
                                                              element: {
                                                                  type: UiSchemaType.NUMBER,
                                                                  label: intl.formatMessage({
                                                                      id: 'activity.field.duration',
                                                                  }),
                                                                  path: 'duration',
                                                                  typeProperties: {
                                                                      integer: true,
                                                                      signed: false,
                                                                  },
                                                              },
                                                          },
                                                          {
                                                              element: valueSchema(
                                                                  {
                                                                      label: intl.formatMessage({
                                                                          id: 'activity.field.durationType',
                                                                      }),
                                                                      path: 'durationType',
                                                                      fetchOptions: getDurationTypes,
                                                                      multiple: false,
                                                                  },
                                                                  intl.locale
                                                              ),
                                                          },
                                                      ],
                                                  },
                                              },
                                          ],
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.LAYOUT_RESPONSIVE,
                                          typeProperties: {
                                              direction: {
                                                  xs: 'row',
                                              },
                                          },
                                          elements: [
                                              {
                                                  element: {
                                                      type: UiSchemaType.TIME,
                                                      label: intl.formatMessage({
                                                          id: 'activity.field.workingTimeAvailStart',
                                                      }),
                                                      path: 'workingTimeAvailStart',
                                                      typeProperties: {
                                                          minutesStep: 30,
                                                      },
                                                  },
                                              },
                                              {
                                                  element: {
                                                      type: UiSchemaType.TIME,
                                                      label: intl.formatMessage({
                                                          id: 'activity.field.workingTimeAvailEnd',
                                                      }),
                                                      path: 'workingTimeAvailEnd',
                                                      typeProperties: {
                                                          minutesStep: 30,
                                                      },
                                                  },
                                              },
                                          ],
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.LAYOUT_RESPONSIVE,
                                          typeProperties: {
                                              direction: {
                                                  xs: 'row',
                                              },
                                          },
                                          elements: [
                                              {
                                                  element: {
                                                      type: UiSchemaType.INPUT,
                                                      label: intl.formatMessage({
                                                          id: 'activity.field.workingTimeAvailTxt',
                                                      }),
                                                      path: 'workingTimeAvailTxt',
                                                      typeProperties: {
                                                          multiline: true,
                                                      },
                                                  },
                                              },
                                              {
                                                  element: {
                                                      type: UiSchemaType.LAYOUT_RESPONSIVE,
                                                      typeProperties: {
                                                          direction: {
                                                              xs: 'row',
                                                          },
                                                      },
                                                      elements: [
                                                          {
                                                              element: {
                                                                  type: UiSchemaType.NUMBER,
                                                                  label: intl.formatMessage({
                                                                      id: 'activity.field.warningDuration',
                                                                  }),
                                                                  path: 'warningDuration',
                                                                  typeProperties: {
                                                                      integer: true,
                                                                      signed: false,
                                                                  },
                                                              },
                                                          },
                                                          {
                                                              element: valueSchema(
                                                                  {
                                                                      label: intl.formatMessage({
                                                                          id: 'activity.field.warningDurationType',
                                                                      }),
                                                                      path: 'warningDurationType',
                                                                      fetchOptions: getDurationTypes,
                                                                      multiple: false,
                                                                  },
                                                                  intl.locale
                                                              ),
                                                          },
                                                      ],
                                                  },
                                              },
                                          ],
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.LAYOUT_RESPONSIVE,
                                          typeProperties: {
                                              direction: {
                                                  xs: 'row',
                                              },
                                          },
                                          elements: [
                                              {
                                                  element: {
                                                      type: UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE,
                                                      label: intl.formatMessage({ id: 'activity.field.earliestStart' }),
                                                      path: 'earliestStart',
                                                  },
                                              },
                                              {
                                                  element: {
                                                      type: UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE,
                                                      label: intl.formatMessage({ id: 'activity.field.latestEnd' }),
                                                      path: 'latestEnd',
                                                  },
                                              },
                                          ],
                                      },
                                  },
                              ],
                          },
                      } satisfies TFormElement,
                  ]
                : []),
            {
                type: UiSchemaType.OPTIONAL_PANEL,
                label: intl.formatMessage({ id: 'activity.panel.when.scheduled' }),
                typeProperties: {
                    icon: <EditCalendar color="action" />,
                    tooltip: intl.formatMessage({
                        id: 'activity.panel.when.scheduled.info',
                    }),
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    elements: [
                        ...(copyPropDateButtonShown
                            ? [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: ({ disabled, readOnly }) => (
                                              <Tooltip
                                                  title={intl.formatMessage({
                                                      id: 'activity.field.prefillScheduledDates.tooltip',
                                                  })}
                                              >
                                                  <span>
                                                      <Button
                                                          disabled={disabled || readOnly}
                                                          variant="outlined"
                                                          startIcon={<ContentCopy />}
                                                          onClick={copyProposedDate}
                                                          fullWidth={true}
                                                          size={'small'}
                                                      >
                                                          <FormattedMessage id="activity.field.prefillScheduledDates" />
                                                      </Button>
                                                  </span>
                                              </Tooltip>
                                          ),
                                      },
                                  } satisfies LayoutResponsiveItem,
                              ]
                            : []),
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'activity.field.whenComments' }),
                                path: 'whenComments',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                typeProperties: {
                                    direction: {
                                        xs: 'row',
                                    },
                                },
                                elements: [
                                    {
                                        element: {
                                            type: UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE,
                                            label: intl.formatMessage({ id: 'activity.field.accessStart' }),
                                            path: 'accessStart',
                                        },
                                    },
                                    {
                                        element: {
                                            type: UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE,
                                            label: intl.formatMessage({ id: 'activity.field.accessEnd' }),
                                            path: 'accessEnd',
                                        },
                                    },
                                ],
                            },
                        },
                        ...(scheduledDatesNotInIntPeriod
                            ? [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: () => (
                                              <Alert severity="warning">
                                                  <FormattedMessage id="activity.field.scheduledInterventionPeriod.warning" />
                                              </Alert>
                                          ),
                                      },
                                  } satisfies LayoutResponsiveItem,
                              ]
                            : []),
                        {
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                typeProperties: {
                                    direction: {
                                        xs: 'row',
                                    },
                                },
                                elements: [
                                    {
                                        element: {
                                            type: UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE,
                                            label: intl.formatMessage({ id: 'activity.field.scheduledStart' }),
                                            path: 'scheduledStart',
                                        },
                                    },
                                    {
                                        element: {
                                            type: UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE,
                                            label: intl.formatMessage({ id: 'activity.field.scheduledEnd' }),
                                            path: 'scheduledEnd',
                                        },
                                    },
                                ],
                            },
                        },
                        {
                            element: interventionPeriodSchema({
                                label: intl.formatMessage({ id: 'activity.field.interventionPeriod' }),
                                path: 'interventionPeriod',
                                multiple: false,
                            }),
                        },
                        {
                            element: valueSchema(
                                {
                                    label: intl.formatMessage({ id: 'activity.field.workingTimeGtd' }),
                                    path: 'workingTimeGtd',
                                    fetchOptions: getWorkingTimes,
                                    multiple: false,
                                },
                                intl.locale
                            ),
                        },
                        {
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                typeProperties: {
                                    direction: {
                                        xs: 'row',
                                    },
                                },
                                elements: [
                                    {
                                        element: {
                                            type: UiSchemaType.TIME,
                                            label: intl.formatMessage({ id: 'activity.field.workingTimeGrtdStart' }),
                                            path: 'workingTimeGrtdStart',
                                            typeProperties: {
                                                minutesStep: 30,
                                            },
                                        },
                                    },
                                    {
                                        element: {
                                            type: UiSchemaType.TIME,
                                            label: intl.formatMessage({ id: 'activity.field.workingTimeGrtdEnd' }),
                                            path: 'workingTimeGrtdEnd',
                                            typeProperties: {
                                                minutesStep: 30,
                                            },
                                        },
                                    },
                                ],
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'activity.field.workingTimeGrtdTxt' }),
                                path: 'workingTimeGrtdTxt',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                    ],
                },
            },
            {
                type: UiSchemaType.PANEL,
                label: intl.formatMessage({ id: 'activity.panel.who' }),
                typeProperties: {
                    icon: <PersonPin color="action" />,
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    typeProperties: {
                        direction: {
                            xs: 'column',
                        },
                    },
                    elements: [
                        ...(isParticipantListEmpty
                            ? [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: () => (
                                              <Alert severity="warning">
                                                  <FormattedMessage id="activity.field.participant.warning" />
                                              </Alert>
                                          ),
                                      },
                                  } satisfies LayoutGridItem,
                              ]
                            : []),
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'activityParticipants',
                                element: uiSchemaParticipant(
                                    intl,
                                    activityStatus,
                                    adamsData,
                                    accessPointsNames,
                                    doseExceedInfo
                                ),
                                appendLabel: intl.formatMessage({
                                    id: 'activity.field.participant.addNewParticipant',
                                }),
                                removeLabel: intl.formatMessage({
                                    id: 'activity.field.participant.removeParticipant',
                                }),
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                typeProperties: {
                                    direction: {
                                        xs: 'row',
                                    },
                                },
                                elements: [
                                    {
                                        element: {
                                            type: UiSchemaType.LAYOUT_RESPONSIVE,
                                            typeProperties: {
                                                direction: {
                                                    xs: 'row',
                                                },
                                            },
                                            elements: [
                                                {
                                                    element: {
                                                        type: UiSchemaType.NUMBER,
                                                        label: intl.formatMessage({
                                                            id: 'activity.field.maxNumberOfParticipants',
                                                        }),
                                                        path: 'maxNbParticipants',
                                                        typeProperties: {
                                                            integer: true,
                                                            signed: false,
                                                        },
                                                    },
                                                },
                                                {
                                                    element: {
                                                        type: UiSchemaType.INPUT,
                                                        label: intl.formatMessage({
                                                            id: 'activity.field.contactPhone',
                                                        }),
                                                        path: 'contactPhone',
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                    {
                                        element: {
                                            type: UiSchemaType.CHECKBOX,
                                            label: intl.formatMessage({
                                                id: 'activity.field.shortTermWorkers',
                                            }),
                                            path: 'shortTermWorkers',
                                            typeProperties: {
                                                endAdornment: () => (
                                                    <TooltipIcon
                                                        tooltip={intl.formatMessage({
                                                            id: 'activity.field.shortTermWorkers.info',
                                                        })}
                                                        documentUrl={URL_EDMS}
                                                    />
                                                ),
                                            },
                                        },
                                    },
                                ],
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'document.panel.comments' }),
                                path: 'whoComments',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                    ],
                },
            },
            {
                type: UiSchemaType.OPTIONAL_PANEL,
                label: intl.formatMessage({ id: 'activity.panel.how' }),
                typeProperties: {
                    icon: <Info color="action" />,
                    tooltip: intl.formatMessage({ id: 'activity.panel.how.info' }),
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    elements: [
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'document.field.modusOperandi' }),
                                path: 'modusOperandi',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'procedureDocuments',
                                element: uiSchemaProcedureDocuments(intl),
                                appendLabel: intl.formatMessage({
                                    id: 'activity.field.activityHazards.addProcedureDocument',
                                }),
                                removeLabel: intl.formatMessage({
                                    id: 'activity.field.activityHazards.removeProcedureDocument',
                                }),
                                tooltip: intl.formatMessage({
                                    id: 'activity.field.activityHazards.procedureDocument.info',
                                }),
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'activity.field.waste' }),
                                path: 'waste',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                elements: [
                                    {
                                        element: valueSchema(
                                            {
                                                label: intl.formatMessage({ id: 'activity.field.constraints' }),
                                                path: 'constraintsList',
                                                fetchOptions: () => getConstraints(),
                                                multiple: true,
                                                endAdornment: () => (
                                                    <TooltipIcon
                                                        tooltip={intl.formatMessage({
                                                            id: 'activity.field.constraints.info',
                                                        })}
                                                    />
                                                ),
                                            },
                                            intl.locale
                                        ),
                                    },
                                    {
                                        element: {
                                            type: UiSchemaType.INPUT,
                                            label: intl.formatMessage({ id: 'activity.field.otherConstraints' }),
                                            path: 'constraints',
                                            typeProperties: {
                                                multiline: true,
                                            },
                                        },
                                    },
                                ],
                                typeProperties: {
                                    direction: {
                                        xs: 'column',
                                        sm: 'row',
                                    },
                                },
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                elements: [
                                    {
                                        element: valueSchema(
                                            {
                                                label: intl.formatMessage({ id: 'activity.field.materialResource' }),
                                                path: 'materialResourceList',
                                                fetchOptions: () => getMaterialResources(),
                                                multiple: true,
                                                endAdornment: () => (
                                                    <TooltipIcon
                                                        tooltip={intl.formatMessage({
                                                            id: 'activity.field.materialResource.info',
                                                        })}
                                                    />
                                                ),
                                            },
                                            intl.locale
                                        ),
                                    },
                                    {
                                        element: {
                                            type: UiSchemaType.INPUT,
                                            label: intl.formatMessage({ id: 'activity.field.otherMaterialResource' }),
                                            path: 'materialResource',
                                            typeProperties: {
                                                multiline: true,
                                            },
                                        },
                                    },
                                ],
                                typeProperties: {
                                    direction: {
                                        xs: 'column',
                                        sm: 'row',
                                    },
                                },
                            },
                        },
                    ],
                },
            },
            {
                type: UiSchemaType.OPTIONAL_PANEL,
                label: intl.formatMessage({ id: 'activity.panel.safety' }),
                typeProperties: {
                    icon: <HealthAndSafety color="action" />,
                    tooltip: intl.formatMessage({ id: 'activity.panel.safety.info' }),
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    elements: [
                        ...(isCreate ||
                        (activityId !== null &&
                            (canLinkSafetyDocuments ||
                                // Hide the component if the user cannot link safety documents and there are none already linked
                                (!!linkedSafetyDocuments &&
                                    (
                                        [
                                            'lockouts',
                                            'firePermits',
                                            'is37s',
                                            'vics',
                                            'notesDeCoupure',
                                        ] satisfies (keyof typeof linkedSafetyDocuments)[]
                                    ).some((key) => linkedSafetyDocuments[key]?.length > 0))))
                            ? [
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: ({ disabled }) => (
                                              <ActivityAddSafetyDocumentButton
                                                  activityId={activityId}
                                                  activityLocations={activityLocations}
                                                  linkedSafetyDocuments={linkedSafetyDocuments}
                                                  title={
                                                      isCreate
                                                          ? intl.formatMessage({
                                                                id: 'activity.field.activityHazards.safetyDocument.infoNeedToCreateFirst',
                                                            })
                                                          : intl.formatMessage({
                                                                id: 'activity.field.activityHazards.safetyDocument.infoLink',
                                                            })
                                                  }
                                                  disabled={disabled || isCreate}
                                                  readOnly={!canLinkSafetyDocuments && !isCreate}
                                                  refreshData={refreshData}
                                              />
                                          ),
                                      } satisfies TFormElement,
                                  },
                              ]
                            : []),
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'safetyDocuments',
                                element: uiSchemaSafetyDocumentsExternal(intl),
                                appendLabel: intl.formatMessage({
                                    id: 'activity.field.activityHazards.addSafetyDocumentExternal',
                                }),
                                removeLabel: intl.formatMessage({
                                    id: 'activity.field.activityHazards.removeSafetyDocumentExternal',
                                }),
                                tooltip: intl.formatMessage({
                                    id: 'activity.field.activityHazards.safetyDocumentExternal.info',
                                }),
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'locationHazards',
                                element: uiSchemaLocationHazards(intl, activityLocations),
                                appendLabel: intl.formatMessage({ id: 'document.field.locationHazards.add' }),
                                removeLabel: intl.formatMessage({ id: 'document.field.locationHazards.remove' }),
                                tooltip: intl.formatMessage({
                                    id: 'document.field.locationHazards.add.info',
                                }),
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'activityHazards',
                                element: uiSchemaActivityHazard(intl),
                                appendLabel: intl.formatMessage({
                                    id: 'activity.field.activityHazards.addNewActivityHazard',
                                }),
                                removeLabel: intl.formatMessage({
                                    id: 'activity.field.activityHazards.removeActivityHazard',
                                }),
                                tooltip: intl.formatMessage({
                                    id: 'activity.field.activityHazards.addNewActivityHazard.info',
                                }),
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'coactivityHazards',
                                element: uiSchemaCoactivityHazard(intl),
                                appendLabel: intl.formatMessage({
                                    id: 'activity.field.coactivityHazard.addNewCoactivityHazard',
                                }),
                                removeLabel: intl.formatMessage({
                                    id: 'activity.field.coactivityHazard.removeCoactivityHazard',
                                }),
                                tooltip: intl.formatMessage({
                                    id: 'activity.field.coactivityHazard.addNewCoactivityHazard.info',
                                }),
                            },
                        },
                    ],
                },
            },
            {
                type: UiSchemaType.OPTIONAL_PANEL,
                label: intl.formatMessage({ id: 'activity.panel.radiography' }),
                typeProperties: {
                    icon: <RadioactiveCircle color="action" />,
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    elements: [
                        {
                            element: valueSchema(
                                {
                                    label: intl.formatMessage({ id: 'activity.field.radiographyDeviceType' }),
                                    path: 'radiographyDeviceType',
                                    fetchOptions: getRadiographyDeviceTypes,
                                    multiple: false,
                                },
                                intl.locale
                            ),
                        },
                        {
                            element: {
                                type: UiSchemaType.INPUT,
                                label: intl.formatMessage({ id: 'activity.field.radiographyXraySource' }),
                                path: 'radiographyXraySource',
                                typeProperties: {
                                    multiline: true,
                                },
                            },
                        },
                        {
                            element: radiographySourceSchema(
                                {
                                    label: intl.formatMessage({ id: 'activity.field.radiographySource' }),
                                    path: 'radiographySource',
                                },
                                intl.locale
                            ),
                        },
                        {
                            element: {
                                type: UiSchemaType.CUSTOM,
                                Component: RadiographySourceLink,
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.CUSTOM,
                                Component: RadiographyAffectedZonePreview,
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.AUTOCOMPLETE,
                                path: 'radiographyAffectedZoneId',
                                label: intl.formatMessage({ id: 'activity.field.radiographyAffectedZone' }),
                                typeProperties: {
                                    options: attachments.map((att) => att.id),
                                    getOptionLabel: (option: AttachmentDTO | number) => {
                                        if (typeof option === 'number') {
                                            //backward compatibility
                                            return attachments.find((a) => a.id === option)?.originalFileName ?? '';
                                        }
                                        return option.originalFileName;
                                    },
                                    autocompleteProps: {
                                        isOptionEqualToValue: (option, value) => option === value,
                                        openOnFocus: true,
                                    },
                                    endAdornment: () => (
                                        <TooltipIcon
                                            tooltip={intl.formatMessage({
                                                id: 'activity.field.radiographyAffectedZone.info',
                                            })}
                                        />
                                    ),
                                },
                            },
                        },
                    ],
                },
            },
            {
                type: UiSchemaType.OPTIONAL_PANEL,
                label: intl.formatMessage({ id: 'activity.panel.rpassessment' }),
                typeProperties: {
                    icon: <Assignment color="action" />,
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    typeProperties: {
                        direction: {
                            xs: 'column',
                        },
                    },
                    elements: [
                        {
                            element: {
                                type: UiSchemaType.LAYOUT_RESPONSIVE,
                                typeProperties: {
                                    direction: {
                                        xs: 'row',
                                    },
                                },
                                elements: [
                                    {
                                        element: numberWithUnitSchemaFor(
                                            {
                                                label: intl.formatMessage({
                                                    id: 'dimr.field.rpa.totalCollectiveWorkTime',
                                                }),
                                                path: 'totalCollectiveWorkingTime',
                                                unit: intl.formatMessage({ id: 'document.unit.personHour' }),
                                                integer: false,
                                                signed: false,
                                            },
                                            intl
                                        ),
                                    },
                                    {
                                        element: numberWithUnitSchemaFor(
                                            {
                                                label: intl.formatMessage({
                                                    id: 'dimr.field.rpa.maxIndividualWorkTime',
                                                }),
                                                path: 'maxIndividualWorkingTime',
                                                unit: intl.formatMessage({ id: 'document.unit.hour' }),
                                                integer: false,
                                                signed: false,
                                            },
                                            intl
                                        ),
                                    },
                                ],
                            },
                        },
                        {
                            element: {
                                type: UiSchemaType.CHECKBOX,
                                label: intl.formatMessage({ id: 'dimr.field.rpa.contaminatingWorks' }),
                                path: 'contaminatingWorks',
                                typeProperties: {
                                    endAdornment: () => (
                                        <TooltipIcon
                                            tooltip={intl.formatMessage({
                                                id: 'dimr.field.rpa.contaminatingWorks.info',
                                            })}
                                        />
                                    ),
                                },
                            },
                        },
                        ...(rpAssessmentLinked
                            ? [
                                  {
                                      element: uiRpAssessment(intl),
                                  },
                              ]
                            : []),
                        {
                            element: {
                                type: UiSchemaType.CHECKBOX,
                                label: intl.formatMessage({ id: 'activity.field.opDosiAll' }),
                                path: 'opDosiAll',
                                typeProperties: {
                                    endAdornment: () => (
                                        <TooltipIcon
                                            tooltip={intl.formatMessage({
                                                id: 'activity.field.opDosiAll.info',
                                            })}
                                        />
                                    ),
                                },
                            },
                        },
                    ],
                },
            },
            // -----------------------ESP-----------------------
            ...(isEspFormEnabled && !!features[FeatureFlag.ESP]
                ? [
                      {
                          type: UiSchemaType.OPTIONAL_PANEL,
                          label: intl.formatMessage({ id: 'activity.panel.espAssessment' }),
                          typeProperties: {
                              icon: <Assignment color="action" />,
                          },
                          element: {
                              type: UiSchemaType.LAYOUT_RESPONSIVE,
                              typeProperties: {
                                  direction: {
                                      xs: 'column',
                                  },
                              },
                              elements: [
                                  {
                                      element: valueSchema(
                                          {
                                              label: intl.formatMessage({
                                                  id: 'activity.field.requestForElectricalRiskAssessment',
                                              }),
                                              path: 'espQuestions.requestForElectricalRiskAssessment',
                                              fetchOptions: getBinaryChoice,
                                              endAdornment: () => (
                                                  <TooltipIcon
                                                      tooltip={intl.formatMessage({
                                                          id: 'activity.field.requestForElectricalRiskAssessment.info',
                                                      })}
                                                  />
                                              ),
                                          },
                                          intl.locale
                                      ),
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.LAYOUT_RESPONSIVE,
                                          typeProperties: {
                                              direction: {
                                                  xs: 'row',
                                              },
                                          },
                                          elements: [
                                              {
                                                  element: {
                                                      type: UiSchemaType.LAYOUT_RESPONSIVE,
                                                      typeProperties: {
                                                          direction: {
                                                              xs: 'column',
                                                          },
                                                      },
                                                      elements: [
                                                          {
                                                              element: valueSchema(
                                                                  {
                                                                      label: intl.formatMessage({
                                                                          id: 'activity.field.modusOperandiDetailLevel',
                                                                      }),
                                                                      path: 'espQuestions.modusOperandiDetailLevel',
                                                                      fetchOptions: getBinaryChoice,
                                                                      endAdornment: () => (
                                                                          <TooltipIcon
                                                                              tooltip={intl.formatMessage({
                                                                                  id: 'activity.field.modusOperandiDetailLevel.info',
                                                                              })}
                                                                          />
                                                                      ),
                                                                  },
                                                                  intl.locale
                                                              ),
                                                          },
                                                          {
                                                              element: {
                                                                  type: UiSchemaType.AUTOCOMPLETE,
                                                                  path: 'espQuestions.modusOperandiDocumentId',
                                                                  label: intl.formatMessage({
                                                                      id: 'activity.field.modusOperandiDocument',
                                                                  }),
                                                                  typeProperties: {
                                                                      options: attachments.map((att) => att.id),
                                                                      getOptionLabel: (
                                                                          option: AttachmentDTO | number
                                                                      ) => {
                                                                          if (typeof option === 'number') {
                                                                              //backward compatibility
                                                                              return (
                                                                                  attachments.find(
                                                                                      (a) => a.id === option
                                                                                  )?.originalFileName ?? ''
                                                                              );
                                                                          }
                                                                          return option.originalFileName;
                                                                      },
                                                                      autocompleteProps: {
                                                                          isOptionEqualToValue: (option, value) =>
                                                                              option === value,
                                                                          openOnFocus: true,
                                                                      },
                                                                      endAdornment: () => (
                                                                          <TooltipIcon
                                                                              tooltip={intl.formatMessage({
                                                                                  id: 'activity.field.modusOperandiDocument.info',
                                                                              })}
                                                                          />
                                                                      ),
                                                                  },
                                                              },
                                                          },
                                                          {
                                                              element: equipmentSchema({
                                                                  label: intl.formatMessage({
                                                                      id: 'lockout.field.equipmentWorkedOn',
                                                                  }),
                                                                  path: 'espQuestions.equipmentWorkedOn',
                                                                  multiple: true,
                                                                  active: true,
                                                              }),
                                                          },
                                                      ],
                                                  },
                                              },
                                              {
                                                  element: {
                                                      type: UiSchemaType.LAYOUT_RESPONSIVE,
                                                      typeProperties: {
                                                          direction: {
                                                              xs: 'column',
                                                          },
                                                      },
                                                      elements: [
                                                          {
                                                              element: {
                                                                  type: UiSchemaType.DATETIME,
                                                                  label: intl.formatMessage({
                                                                      id: 'lockout.field.requestStart.mise-en-securite',
                                                                  }),
                                                                  path: 'espQuestions.lockoutRequestStartDate',
                                                                  typeProperties: {
                                                                      minDate: new Date(),
                                                                  },
                                                              },
                                                          },
                                                          {
                                                              element: {
                                                                  type: UiSchemaType.DATETIME,
                                                                  label: intl.formatMessage({
                                                                      id: 'lockout.field.requestEnd.mise-en-securite',
                                                                  }),
                                                                  path: 'espQuestions.lockoutRequestEndDate',
                                                                  typeProperties: {
                                                                      minDate: new Date(),
                                                                  },
                                                              },
                                                          },
                                                          {
                                                              element: {
                                                                  type: UiSchemaType.INPUT,
                                                                  label: intl.formatMessage({
                                                                      id: 'lockout.field.equipmentWorkedOn.details',
                                                                  }),
                                                                  path: 'espQuestions.equipmentWorkedOnText',
                                                              },
                                                          },
                                                      ],
                                                  },
                                              },
                                          ],
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.RICH_TEXT,
                                          label: intl.formatMessage({
                                              id: 'activity.field.otherHazards',
                                          }),
                                          path: 'espQuestions.otherHazards',
                                      },
                                  },
                              ],
                          },
                      } satisfies TFormElement,
                  ]
                : []),
            // -----------------------ESP Preassessed Template -----------------------
            ...(isEspFormEnabled && isFromEspTemplate && !!features[FeatureFlag.ESP]
                ? [
                      {
                          type: UiSchemaType.OPTIONAL_PANEL,
                          label: intl.formatMessage({ id: 'activity.panel.espPreassessment' }),
                          typeProperties: {
                              icon: <Assignment color="action" />,
                          },
                          element: {
                              type: UiSchemaType.LAYOUT_RESPONSIVE,
                              typeProperties: {
                                  direction: {
                                      xs: 'column',
                                  },
                              },
                              elements: [
                                  {
                                      element: {
                                          type: UiSchemaType.INPUT,
                                          label: intl.formatMessage({ id: 'activity.field.parentTemplateLabel' }),
                                          path: 'activityParent.title',
                                          readOnly: true,
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: ({ disabled }) => {
                                              const { control, register } = useFormContext<ActivityNimReadDTO>();
                                              const path = 'activityParent.description';
                                              const { ref, ...registered } = register(path);
                                              return (
                                                  <Controller
                                                      key={path}
                                                      control={control}
                                                      {...registered}
                                                      render={({ field, fieldState }) => (
                                                          <Input
                                                              field={field as any}
                                                              label={intl.formatMessage({
                                                                  id: 'activity.field.parentTemplateDescription',
                                                              })}
                                                              readOnly={true}
                                                              multiline={true}
                                                              error={fieldState.error}
                                                              disabled={disabled}
                                                          />
                                                      )}
                                                  />
                                              );
                                          },
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.CUSTOM,
                                          Component: () => (
                                              <TextField
                                                  value={intl.formatMessage({
                                                      id: 'activity.content.preassessmentUseAgreement',
                                                  })}
                                                  fullWidth
                                                  multiline
                                                  InputProps={{
                                                      readOnly: true,
                                                  }}
                                                  size="small"
                                              />
                                          ),
                                      },
                                  },
                                  {
                                      element: {
                                          type: UiSchemaType.CHECKBOX,
                                          label: intl.formatMessage({
                                              id: 'activity.field.preassessmentConfirmation',
                                          }),
                                          path: 'preassessmentConfirmation',
                                      },
                                  },
                              ],
                          },
                      } satisfies TFormElement,
                  ]
                : []),
            // -----------------------End of ESP-----------------------
            {
                type: UiSchemaType.OPTIONAL_PANEL,
                label: intl.formatMessage({ id: 'activity.panel.tests' }),
                typeProperties: {
                    icon: <Checklist color="action" />,
                },
                element: {
                    type: UiSchemaType.LAYOUT_RESPONSIVE,
                    typeProperties: {
                        direction: {
                            xs: 'column',
                        },
                    },
                    elements: [
                        {
                            element: valueSchema(
                                {
                                    label: intl.formatMessage({ id: 'activity.field.alarms' }),
                                    path: 'triggeredAlarms',
                                    fetchOptions: getTriggeredAlarmTypes,
                                    multiple: true,
                                },
                                intl.locale
                            ),
                        },
                        {
                            element: {
                                type: UiSchemaType.FIELD_ARRAY,
                                path: 'testQuestions',
                                element: uiSchemaActivityTest(intl, areTestQuestionsDisabled),
                                appendLabel: '',
                                removeLabel: '',
                                initialValue: {},
                                shallowReadOnly: true,
                            },
                        },
                    ],
                },
            },

            //TODO

            // {
            //     type: UiSchemaType.PANEL,
            //     label: intl.formatMessage({ id: 'activity.panel.logbook' }),
            //     element: {
            //         type: UiSchemaType.FIELD_ARRAY,
            //         path: 'onSiteActivityComments',
            //         element: uiSchemaLogBookEntry(intl),
            //         appendLabel: '',
            //         removeLabel: '',
            //         shallowReadOnly: true,
            //     },
            //     typeProperties: {
            //         icon: <MenuBook color="action" />,
            //     },
            // },
            {
                type: UiSchemaType.PANEL,
                label: intl.formatMessage({ id: 'activity.panel.workOrders' }),
                element: {
                    type: UiSchemaType.LAYOUT_GRID,
                    elements: [
                        {
                            xs: 12,
                            element: workOrdersSchema({
                                label: intl.formatMessage({ id: 'activity.field.workOrders' }),
                                path: 'workOrders',
                                disabled: true,
                            }),
                        },
                    ],
                },
                typeProperties: {
                    icon: <WorkOrderIcon color="action" />,
                },
            },
            // {
            //     type: UiSchemaType.PANEL,
            //     label: intl.formatMessage({ id: 'activity.panel.logbook' }),
            // },
        ],
    };
};
